/**
 * MarkdownLite — lightweight Markdown renderer for reader/supervisor text:
 * **bold**, bullet/numbered lists, headings-as-bold, paragraph breaks.
 * Optionally turns "Scene N" mentions into clickable jump links when
 * sceneMap + onJump are provided.
 */
import React from "react";

function SceneLinks({ text, sceneMap, onJump }) {
  if (!sceneMap || !onJump) return <span>{text}</span>;
  const parts = [];
  const re = /Scene\s+(\d+)/gi;
  let last = 0, m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const n = parseInt(m[1], 10);
    const target = sceneMap[n - 1];
    if (target) {
      parts.push(
        <button key={m.index} onClick={() => onJump(target.id)} className="text-[var(--brand)] underline underline-offset-2 hover:opacity-80" data-testid={`supervisor-cite-scene-${n}`}>{m[0]}</button>
      );
    } else parts.push(m[0]);
    last = m.index + m[0].length;
  }
  parts.push(text.slice(last));
  return <span>{parts}</span>;
}

function renderInline(text, sceneMap, onJump, keyBase) {
  const out = [];
  const boldRe = /\*\*(.+?)\*\*/g;
  let last = 0, m, k = 0;
  const pushText = (t) => {
    if (!t) return;
    const itRe = /\*([^*\n]+)\*/g;
    let l2 = 0, mm;
    while ((mm = itRe.exec(t)) !== null) {
      if (mm.index > l2) out.push(<SceneLinks key={`${keyBase}-t${k++}`} text={t.slice(l2, mm.index)} sceneMap={sceneMap} onJump={onJump} />);
      out.push(<em key={`${keyBase}-i${k++}`}><SceneLinks text={mm[1]} sceneMap={sceneMap} onJump={onJump} /></em>);
      l2 = mm.index + mm[0].length;
    }
    if (l2 < t.length) out.push(<SceneLinks key={`${keyBase}-t${k++}`} text={t.slice(l2)} sceneMap={sceneMap} onJump={onJump} />);
  };
  while ((m = boldRe.exec(text)) !== null) {
    pushText(text.slice(last, m.index));
    out.push(<strong key={`${keyBase}-b${k++}`} className="font-semibold"><SceneLinks text={m[1]} sceneMap={sceneMap} onJump={onJump} /></strong>);
    last = m.index + m[0].length;
  }
  pushText(text.slice(last));
  return out;
}

export function MarkdownLite({ text, sceneMap, onJump, className = "" }) {
  const lines = (text || "").split("\n");
  const blocks = [];
  let list = null;
  const flush = () => { if (list) { blocks.push(list); list = null; } };
  for (const raw of lines) {
    const line = raw.trimEnd();
    const bullet = line.match(/^\s*(?:[-•]|\*(?!\*))\s+(.*)$/);
    const num = line.match(/^\s*(\d+)[.)]\s+(.*)$/);
    const head = line.match(/^\s*#{1,4}\s+(.*)$/);
    if (bullet) {
      if (!list || list.type !== "ul") { flush(); list = { type: "ul", items: [] }; }
      list.items.push(bullet[1]);
    } else if (num) {
      if (!list || list.type !== "ol") { flush(); list = { type: "ol", items: [] }; }
      list.items.push(num[2]);
    } else {
      flush();
      if (head) blocks.push({ type: "h", text: head[1] });
      else if (line.trim()) blocks.push({ type: "p", text: line });
    }
  }
  flush();
  return (
    <div className={`space-y-1.5 ${className}`}>
      {blocks.map((b, i) => {
        if (b.type === "ul") return (
          <ul key={i} className="list-disc pl-4 space-y-1">
            {b.items.map((it, j) => <li key={j}>{renderInline(it, sceneMap, onJump, `${i}-${j}`)}</li>)}
          </ul>
        );
        if (b.type === "ol") return (
          <ol key={i} className="list-decimal pl-4 space-y-1">
            {b.items.map((it, j) => <li key={j}>{renderInline(it, sceneMap, onJump, `${i}-${j}`)}</li>)}
          </ol>
        );
        if (b.type === "h") return <p key={i} className="font-semibold">{renderInline(b.text, sceneMap, onJump, `${i}`)}</p>;
        return <p key={i}>{renderInline(b.text, sceneMap, onJump, `${i}`)}</p>;
      })}
    </div>
  );
}
